// 📋 MyWorkPanel — barista's personal page.
//
// Current scope: one sub-section "🗑 طلبات الإتلاف" (damage requests). The
// panel is designed so future sub-sections (training file, evaluations,
// notifications, etc.) can be added as extra pills without restructuring.
//
// Workflow:
//   barista submits → branch_manager approves → operations approves+exports.
// The Excel + PDF appear only AFTER operations final approval.
import React, { useEffect, useState, useRef } from "react";
import { api } from "./lib/api";
import { showToast as showToastRaw } from "./lib/theme";
import { formatErr } from "./lib/api";
import { useAuth } from "./lib/auth";
import BranchProductsForecast from "./BranchProductsForecast";
import { InfoTip } from "./components/InfoTip";

const showToast = (msg, kind = "info") => {
  try { showToastRaw(msg, kind); } catch (_e) { console.log(`[${kind}]`, msg); }
};
const fmErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join("، ");
  return e?.message || "حدث خطأ";
};

const STATE_LABELS = {
  pending_branch:   { label: "بانتظار مدير الفرع",   color: "#D97706", bg: "#FEF3C7" },
  pending_manager:  { label: "بانتظار مدير الفرع",   color: "#D97706", bg: "#FEF3C7" }, // v187 (alias)
  pending_ops:      { label: "بانتظار المالية",      color: "#0891B2", bg: "#CFFAFE" }, // v187 — الاسم القديم أصبح "المالية"
  pending_finance:  { label: "بانتظار المالية",      color: "#0891B2", bg: "#CFFAFE" }, // v187
  approved:         { label: "✓ معتمد نهائيًا",     color: "#16A34A", bg: "#DCFCE7" },
  approved_final:   { label: "✓ معتمد نهائيًا",     color: "#16A34A", bg: "#DCFCE7" }, // v187
  archived:         { label: "📦 مؤرشف",             color: "#7C3AED", bg: "#EDE9FE" },
  rejected:         { label: "❌ مرفوض",             color: "#DC2626", bg: "#FEE2E2" },
};

const SECTIONS = [
  { id: "profile", label: "👤 ملفي الشخصي" },
  { id: "today", label: "🔮 توقع اليوم" },
  { id: "damage", label: "🗑 طلبات الإتلاف" },
];

export default function MyWorkPanel() {
  const [sec, setSec] = useState("profile");
  return (
    <div data-testid="my-work-panel" style={{ padding: 14 }}>
      <div style={{ background: "linear-gradient(135deg, #6b4423 0%, #b8860b 100%)", color: "#fff8e6",
        padding: "14px 18px", borderRadius: 10, marginBottom: 14 }}>
        <div style={{ fontSize: 18, fontWeight: 800 }}>📋 موظف الفرع</div>
        <div style={{ fontSize: 12, opacity: 0.9, marginTop: 4 }}>
          صفحتك الميدانية — توقع منتجات اليوم وطلبات الإتلاف وملفك الشخصي
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {SECTIONS.map((s) => (
          <button key={s.id} onClick={() => setSec(s.id)} data-testid={`my-work-sec-${s.id}`}
            style={{
              padding: "8px 14px", borderRadius: 999,
              background: sec === s.id ? "var(--brown-deep)" : "#fff",
              color: sec === s.id ? "#fff" : "var(--brown-deep)",
              border: `1px solid ${sec === s.id ? "var(--brown-deep)" : "var(--border)"}`,
              fontWeight: 700, cursor: "pointer", fontSize: 13,
            }}>
            {s.label}
          </button>
        ))}
      </div>

      {sec === "today" && (
        <div data-testid="my-work-today" style={{ background: "#fff", borderRadius: 10,
          padding: 14, border: "1px solid var(--border)" }}>
          <BranchProductsForecast variant="daily" topN={20} />
        </div>
      )}
      {sec === "damage" && <DamageRequestsPanel />}
      {sec === "profile" && <EmployeeProfilePanel />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Damage Requests
// ────────────────────────────────────────────────────────────────────
function DamageRequestsPanel() {
  const { user } = useAuth() || {};
  const userBranchId = user?.branch_id || "";
  const [products, setProducts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [branch, setBranch] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const selectedProduct = products.find((p) => p.id === productId);

  const loadProducts = async () => {
    try {
      const { data } = await api.get("/products/catalog", { params: { active_only: true, limit: 500 } });
      setProducts(data.items || []);
    } catch (e) { showToast(fmErr(e), "error"); }
  };
  const loadRequests = async () => {
    try {
      const { data } = await api.get("/damage-requests", { params: { mine: true, limit: 100 } });
      setRequests(data.items || []);
    } catch (e) { showToast(fmErr(e), "error"); }
  };
  useEffect(() => { loadProducts(); loadRequests(); }, []);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      showToast("الصورة أكبر من 8 ميجا — اختر صورة أصغر", "error");
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!productId) { showToast("اختر المنتج من القائمة", "error"); return; }
    if (!quantity || Number(quantity) <= 0) { showToast("أدخل كمية صحيحة", "error"); return; }
    if (!reason.trim()) { showToast("أدخل سبب الإتلاف", "error"); return; }
    if (!photoFile) { showToast("الصورة مطلوبة — المكتب المحاسبي يشترطها كإثبات", "error"); return; }
    if (!userBranchId) {
      showToast("حسابك ليس مرتبطًا بفرع بعد — تواصل مع الأدمن لإضافة branch_id لحسابك", "error");
      return;
    }
    const fd = new FormData();
    fd.append("product_id", productId);
    fd.append("quantity", quantity);
    fd.append("reason", reason);
    fd.append("notes", notes);
    fd.append("branch", branch);
    fd.append("branch_id", userBranchId);  // v187 — فرع-واعٍ منذ التقديم
    fd.append("photo", photoFile);
    setBusy(true);
    try {
      await api.post("/damage-requests", fd, { headers: { "Content-Type": "multipart/form-data" } });
      showToast("✅ تم رفع طلب الإتلاف — بانتظار اعتماد مدير الفرع", "success");
      // reset
      setProductId(""); setQuantity(""); setReason(""); setNotes(""); setBranch("");
      setPhotoFile(null); setPhotoPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      loadRequests();
    } catch (e) { showToast(fmErr(e), "error"); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 14 }}>
      {/* Form */}
      <div className="card" style={{ padding: 16 }} data-testid="damage-form">
        <div style={{ fontSize: 15, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 10 }}>
          🗑 رفع طلب إتلاف جديد
        </div>
        <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 14, lineHeight: 1.7,
          background: "#FEF3C7", padding: 10, borderRadius: 6 }}>
          ⚠️ جميع الحقول إلزامية — المكتب المحاسبي يشترط صورة المنتج التالف كإثبات
        </div>

        <Field label="المنتج *">
          <select value={productId} onChange={(e) => setProductId(e.target.value)} data-testid="dmg-product"
            style={inputStyle}>
            <option value="">— اختر منتج —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>
            ))}
          </select>
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 80px", gap: 6 }}>
          <Field label={`كمية الهدر * (${selectedProduct ? unitArabic(selectedProduct.unit) : "—"})`}>
            <input type="number" min="0.01" step="0.01" value={quantity}
              onChange={(e) => setQuantity(e.target.value)} data-testid="dmg-qty"
              style={inputStyle} placeholder="مثال: 250" />
          </Field>
          <Field label="الوحدة">
            <input value={selectedProduct ? unitArabic(selectedProduct.unit) : ""} readOnly data-testid="dmg-unit"
              style={{ ...inputStyle, background: "#f3f4f6", color: "var(--muted)" }} />
          </Field>
        </div>

        <Field label="السبب *">
          <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dmg-reason"
            style={inputStyle}>
            <option value="">— اختر السبب —</option>
            <option>انتهاء صلاحية</option>
            <option>كسر / تسرّب</option>
            <option>خطأ في التحضير</option>
            <option>سوء تخزين</option>
            <option>طلب عميل ملغى بعد التحضير</option>
            <option>تلف بالنقل</option>
            <option>أخرى — مذكورة بالملاحظات</option>
          </select>
        </Field>

        <Field label="الفرع">
          <input value={branch} onChange={(e) => setBranch(e.target.value)} data-testid="dmg-branch"
            style={inputStyle} placeholder="مثال: فرع شارع الأمير سلطان" />
        </Field>

        <Field label="ملاحظات">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="dmg-notes"
            rows={2} style={{ ...inputStyle, resize: "vertical" }}
            placeholder="تفاصيل إضافية لمدير الفرع والمحاسبة" />
        </Field>

        <Field label="📸 صورة المنتج التالف * (إلزامية)">
          <label htmlFor="dmg-photo-input" data-testid="dmg-photo-dropzone"
            style={{
              display: "block", padding: 18,
              border: "2px dashed var(--brown-deep)",
              borderRadius: 10, background: photoPreview ? "#fff" : "#FEFBF3",
              textAlign: "center", cursor: "pointer",
              transition: "background 0.2s",
            }}>
            {!photoPreview && (
              <>
                <div style={{ fontSize: 42, marginBottom: 6 }}>📷</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 4 }}>
                  انقر لاختيار صورة المنتج التالف
                </div>
                <div style={{ fontSize: 11.5, color: "var(--muted)" }}>
                  JPG · PNG · WebP · HEIC — حتى 8 ميجا
                </div>
              </>
            )}
            {photoPreview && (
              <>
                <img src={photoPreview} alt="معاينة" data-testid="dmg-photo-preview"
                  style={{ maxWidth: "100%", maxHeight: 240, borderRadius: 8, marginBottom: 8 }} />
                <div style={{ fontSize: 12, color: "var(--brown-deep)", fontWeight: 700 }}>
                  ✓ {photoFile?.name} — انقر للتغيير
                </div>
              </>
            )}
            <input ref={fileRef} id="dmg-photo-input" type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              onChange={handlePhoto} data-testid="dmg-photo"
              style={{ display: "none" }} />
          </label>
        </Field>

        <button onClick={submit} disabled={busy} data-testid="dmg-submit"
          style={{ width: "100%", padding: 12, marginTop: 10, background: "var(--brown-deep)",
            color: "#fff", border: "none", borderRadius: 6, fontWeight: 800, cursor: busy ? "wait" : "pointer", fontSize: 14 }}>
          {busy ? "⏳ جارٍ الإرسال..." : "📤 إرسال طلب الإتلاف"}
        </button>
      </div>

      {/* My requests list */}
      <div data-testid="my-damage-list">
        <div style={{ fontSize: 14, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 8 }}>
          📜 طلباتي السابقة ({requests.length})
        </div>
        {requests.length === 0 && (
          <div className="card" style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>
            لا توجد طلبات بعد. سجّل أول طلب إتلاف من النموذج.
          </div>
        )}
        {requests.map((r) => <DamageCard key={r.id} req={r} canApprove={false} onChanged={loadRequests} />)}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Reusable DamageCard — list view used in both barista's "mine" list
// and in managers' admin panel (with approve buttons).
// ────────────────────────────────────────────────────────────────────
export function DamageCard({ req, canApprove = false, role = "barista", onChanged, isAdmin = false }) {
  const [expanded, setExpanded] = useState(false);
  const st = STATE_LABELS[req.status] || { label: req.status, color: "#6B7280", bg: "#F3F4F6" };
  const photoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/damage-requests/${req.id}/photo`;

  const doApprove = async (stage) => {
    const stageLabel = stage === "branch" ? "مدير الفرع" : "المالية النهائي";
    if (!window.confirm(`اعتماد الطلب في مرحلة ${stageLabel}؟`)) return;
    try {
      // v187 — /approve-finance بديل /approve-ops (backend يوفّر alias للتوافق).
      const url = stage === "finance"
        ? `/damage-requests/${req.id}/approve-finance`
        : `/damage-requests/${req.id}/approve-${stage}`;
      await api.post(url, { note: "" });
      showToast("✅ تم الاعتماد", "success");
      onChanged && onChanged();
    } catch (e) { showToast(fmErr(e), "error"); }
  };
  const doReject = async () => {
    const note = window.prompt("سبب الرفض (إلزامي):");
    if (!note || note.trim().length < 2) return;
    try {
      await api.post(`/damage-requests/${req.id}/reject`, { note });
      showToast("تم رفض الطلب", "success");
      onChanged && onChanged();
    } catch (e) { showToast(fmErr(e), "error"); }
  };
  const doAdminDelete = async () => {
    const warn = `⚠️ حذف نهائي — هذا الإجراء غير قابل للتراجع.\n\n` +
                 `سيُحذف الطلب من النظام ومن صورة المنتج ومن ملف الإتلاف المؤرشف ` +
                 `(إذا كان مضمَّناً، سيُعاد بناء الـ Excel/PDF تلقائياً).\n\nتأكيد؟`;
    if (!window.confirm(warn)) return;
    const typed = window.prompt(`اكتب «حذف» للتأكيد النهائي:`);
    if (typed !== "حذف") { showToast("ألغي الحذف", "info"); return; }
    try {
      const { data } = await api.delete(`/damage-requests/${req.id}`);
      showToast(
        data.archive_rebuilt
          ? "✅ حُذف الطلب وأُعيد بناء ملف الأرشيف"
          : "✅ حُذف الطلب نهائياً",
        "success",
      );
      onChanged && onChanged();
    } catch (e) { showToast(fmErr(e), "error"); }
  };

  return (
    <div className="card" style={{ padding: 12, marginBottom: 8 }} data-testid={`dmg-card-${req.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 4 }}>
            {req.product_name} — {req.quantity} {unitArabic(req.product_unit)}
          </div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>
            {req.reason} · {req.branch || "بدون فرع"} · {req.submitted_at?.slice(0, 10)}
          </div>
          {req.notes && <div style={{ fontSize: 11.5, color: "var(--muted)", fontStyle: "italic" }}>📝 {req.notes}</div>}
        </div>
        <span style={{ padding: "4px 10px", borderRadius: 999, background: st.bg, color: st.color, fontWeight: 700, fontSize: 11, whiteSpace: "nowrap" }}
          data-testid={`dmg-status-${req.id}`}>
          {st.label}
        </span>
      </div>

      {/* Action row */}
      <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
        <button onClick={() => setExpanded((x) => !x)} data-testid={`dmg-toggle-${req.id}`}
          style={btnSm}>{expanded ? "إخفاء" : "👁 عرض"}</button>

        {canApprove && role === "branch_manager" && (req.status === "pending_branch" || req.status === "pending_manager") && (
          <>
            <button onClick={() => doApprove("branch")} data-testid={`dmg-approve-branch-${req.id}`}
              style={{ ...btnSm, background: "#16A34A", color: "#fff" }}>✓ اعتماد الفرع</button>
            <button onClick={doReject} data-testid={`dmg-reject-${req.id}`}
              style={{ ...btnSm, background: "#DC2626", color: "#fff" }}>✗ رفض</button>
          </>
        )}
        {/* v187 — الدور المالي الجديد بديل التشغيل. */}
        {canApprove && role === "accountant" && (req.status === "pending_ops" || req.status === "pending_finance") && (
          <>
            <button onClick={() => doApprove("finance")} data-testid={`dmg-approve-finance-${req.id}`}
              style={{ ...btnSm, background: "#16A34A", color: "#fff" }}>✓ اعتماد المالية النهائي</button>
            <button onClick={doReject} data-testid={`dmg-reject-${req.id}`}
              style={{ ...btnSm, background: "#DC2626", color: "#fff" }}>✗ رفض</button>
          </>
        )}
        {/* Legacy: role=operations كان يعتمد سابقًا. v187 خصص الاعتماد للمالية،
            لكن نُبقي الزر للأدمن فقط (isAdmin) لأنه دستوريًا شامل الصلاحيات. */}
        {canApprove && role === "admin_ops_legacy" && (req.status === "pending_ops" || req.status === "pending_finance") && (
          <>
            <button onClick={() => doApprove("finance")} data-testid={`dmg-approve-ops-${req.id}`}
              style={{ ...btnSm, background: "#16A34A", color: "#fff" }}>✓ اعتماد نهائي (أدمن)</button>
            <button onClick={doReject} data-testid={`dmg-reject-${req.id}`}
              style={{ ...btnSm, background: "#DC2626", color: "#fff" }}>✗ رفض</button>
          </>
        )}
        {role === "operations_observer" && (
          <span style={{ ...btnSm, background: "#F3F4F6", color: "#6B7280", display: "inline-flex", alignItems: "center" }}
 ><InfoTip text="مدير التشغيل مراقب فقط في v187 — الاعتماد النهائي أصبح للمالية" />
            👁 مراقبة فقط
          </span>
        )}

        {(req.status === "approved" || req.status === "approved_final") && (
          <span style={{ ...btnSm, background: "#16A34A", color: "#fff", display: "inline-flex", alignItems: "center" }}>
            ✓ معتمد نهائيًا{req.total_value != null && ` — ${req.total_value} ر.س`}
          </span>
        )}
        {req.status === "archived" && (
          <span style={{ ...btnSm, background: "#7C3AED", color: "#fff", display: "inline-flex", alignItems: "center" }}>
            📦 مؤرشف — في صفحة التالف
          </span>
        )}

        {/* Admin can hard-delete any damage request regardless of state.
            Confirmation gate is double — confirm() + prompt() typed "حذف". */}
        {isAdmin && (
          <button onClick={doAdminDelete} data-testid={`dmg-admin-del-${req.id}`}
            style={{ ...btnSm, background: "#7F1D1D", color: "#fff",
                     marginInlineStart: "auto", fontWeight: 800 }}
  aria-label="حذف نهائي (أدمن فقط) — يُعيد بناء ملف الأرشيف تلقائياً">
            🗑 حذف نهائي
          </button>
        )}
      </div>

      {expanded && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
          <img src={photoUrl} alt="إثبات الإتلاف" data-testid={`dmg-photo-${req.id}`}
            style={{ maxWidth: "100%", maxHeight: 280, borderRadius: 6, marginBottom: 8 }}
            onError={(e) => { e.target.style.display = "none"; }} />
          <div style={{ fontSize: 11.5, fontWeight: 700, marginBottom: 4 }}>📜 سجل المراحل</div>
          {(req.timeline || []).map((t, i) => (
            <div key={i} style={{ fontSize: 11.5, padding: "4px 8px", marginBottom: 3, background: "#FEFBF3", borderRadius: 4 }}>
              <b>{translateAction(t.action)}</b> — {t.by} · {t.at?.slice(0, 16).replace("T", " ")}
              {t.note && <span style={{ color: "var(--muted)" }}> — {t.note}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function EmployeeProfilePanel() {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let alive = true;
    api.get("/employee/me/profile")
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setErr(formatErr(e?.response?.data?.detail) || e?.message || "خطأ"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <div className="card" style={{ padding: 22, textAlign: "center", color: "var(--muted)" }} data-testid="profile-loading">جاري التحميل…</div>;
  }
  if (err) {
    return <div className="card" style={{ padding: 16, background: "var(--danger-soft)", color: "var(--danger)" }} data-testid="profile-error">{err}</div>;
  }
  if (!data) return null;

  const u = data.user || {};
  const ev = data.latest_evaluation;
  const wn = data.warnings || { count: 0, items: [] };
  const tr = data.trainings || {};
  const att = data.attendance;
  const evs = data.evaluations_year || [];
  const bn = data.bonus;
  const elig = bn?.eligibility;
  const isManagerRole = u.role === "branch_manager";
  const isBaristaLike = u.role === "barista" || u.role === "admin";
  const myBonusRaw = bn ? (isManagerRole ? bn.per_manager : isBaristaLike ? bn.per_barista : 0) : 0;
  const myBonus = elig?.is_eligible === false ? 0 : myBonusRaw;
  const shareLabel = isManagerRole ? "نصيبي كمدير" : isBaristaLike ? "نصيبي كباريستا" : "نصيب الموظف";
  const pickWhatIfShare = (s) => isManagerRole ? s.per_manager : s.per_barista;

  return (
    <div style={{ display: "grid", gap: 12 }} dir="rtl" data-testid="employee-profile">
      {/* Identity card */}
      <div className="card" style={{ padding: 16, display: "flex", gap: 14, alignItems: "center" }}>
        <div style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--cream-warm)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>👤</div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "var(--brown-deep)" }}>{u.full_name || u.username}</div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
            {u.role && <span>الدور: <b>{u.role}</b></span>}
            {u.branch && <span> · الفرع: <b>{u.branch}</b></span>}
            {u.employee_number && <span> · رقم الموظف: <b>{u.employee_number}</b></span>}
          </div>
        </div>
      </div>

      {/* Bonus progress card */}
      {bn && (
        <div className="card" style={{ padding: 16 }} data-testid="profile-bonus">
          <h3 style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>💰 بونص الشهر — {bn.year_month}</h3>

          {/* Eligibility banner (3-month tenure rule) */}
          {elig && !elig.is_eligible && (
            <div data-testid="bonus-eligibility-banner" style={{ padding: 10, background: "#fef3c7", color: "#92400e", borderRadius: 8, fontSize: 12.5, marginBottom: 12, lineHeight: 1.7 }}>
              🔒 <b>أنت لست مؤهلاً للبونص بعد.</b> يستحق البونص لكل موظف بعد إتمام {elig.min_days_required} يوم من تاريخ الانضمام.
              <div style={{ marginTop: 4 }}>
                {elig.hire_date ? <>تاريخ الانضمام: <b>{elig.hire_date}</b> · </> : null}
                مرّ على انضمامك <b>{elig.tenure_days}</b> يوم · باقي <b>{elig.days_until_eligible}</b> يوم على استحقاقك.
              </div>
              {elig.source === "malachite_hcm" && <div style={{ marginTop: 3, fontSize: 10, color: "var(--muted)" }}>📡 المصدر: Malachite HCM</div>}
              {elig.source === "missing" && <div style={{ marginTop: 3, fontSize: 10, color: "#b45309" }}>⚠ تاريخ الانضمام غير موجود — يستخدم تاريخ إنشاء الحساب</div>}
            </div>
          )}
          {elig && elig.is_eligible && (
            <div style={{ fontSize: 11, color: "var(--success)", marginBottom: 8 }}>
              ✓ مؤهل للبونص — مرّ {elig.tenure_days} يوم من انضمامك
              {elig.source === "malachite_hcm" && <span style={{ color: "var(--muted)" }}> (Malachite HCM)</span>}
            </div>
          )}

          {/* Progress bar */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
              <span style={{ color: "var(--muted)" }}>تحقّق <b style={{ color: bn.target_reached ? "var(--success)" : "var(--brown-deep)", fontSize: 18 }}>{bn.progress_pct}%</b> من الهدف</span>
              <span style={{ color: "var(--muted)" }}>{bn.month_sales.toLocaleString("en-US")} / {bn.monthly_target.toLocaleString("en-US")} ر</span>
            </div>
            <div style={{ height: 10, background: "var(--beige-soft)", borderRadius: 999, overflow: "hidden" }}>
              <div style={{
                height: "100%",
                width: `${bn.progress_pct}%`,
                background: bn.target_reached ? "var(--success)" : "linear-gradient(90deg, var(--brown-soft), var(--brown-deep))",
                transition: "width 400ms ease",
              }} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
            <MetricMini label={shareLabel} value={`${Math.round(myBonus).toLocaleString("en-US")} ر`} color={bn.target_reached && elig?.is_eligible !== false ? "var(--success)" : "var(--muted)"} />
            <MetricMini label="إجمالي البونص" value={`${Math.round(bn.bonus_pool).toLocaleString("en-US")} ر`} color={bn.target_reached ? "var(--success)" : "var(--text)"} />
            <MetricMini label="نسبة البونص" value={`${bn.bonus_pct}%`} />
            <MetricMini label={bn.target_reached ? "تم تحقيق الهدف ✅" : "متبقي للهدف"} value={bn.target_reached ? "🎉" : `${Math.round(bn.monthly_target - bn.month_sales).toLocaleString("en-US")} ر`} color={bn.target_reached ? "var(--success)" : "var(--warn)"} />
          </div>

          <div style={{ marginTop: 10, fontSize: 11, color: "var(--muted)", textAlign: "center" }}>
            {bn.target_reached
              ? `✅ ${bn.bonus_pct}% من المبيعات · ${(100 - bn.manager_share_pct).toFixed(0)}% يقسم على ${bn.baristas} باريستا · ${bn.manager_share_pct}% للمدير`
              : `📐 ${bn.bonus_pct}% بونص عند تحقيق ${bn.monthly_target.toLocaleString("en-US")} ر`}
          </div>

          {/* What-if motivational scenarios */}
          {(bn.what_if || []).length > 0 && (
            <div style={{ marginTop: 14, borderTop: "1px dashed var(--border)", paddingTop: 12 }} data-testid="bonus-what-if">
              <h4 style={{ fontSize: 13, fontWeight: 800, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                🚀 <span>كم تأخذ لو حققنا أكثر؟</span>
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
                {bn.what_if.map((s, i) => {
                  const myShare = pickWhatIfShare(s);
                  const isCurrentBest = i === bn.what_if.length - 1;
                  return (
                    <div key={i} style={{
                      padding: 12,
                      background: isCurrentBest ? "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)" : "var(--beige-soft)",
                      borderRadius: 10,
                      textAlign: "center",
                      border: isCurrentBest ? "1.5px solid #f59e0b" : "1px solid transparent",
                    }} data-testid={`bonus-scenario-${i}`}>
                      <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>{s.label}</div>
                      <div style={{ fontSize: 13, color: "var(--brown-deep)", marginBottom: 6 }}>
                        مبيعات: <b>{Math.round(s.sales).toLocaleString("en-US")}</b> ر
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: elig?.is_eligible === false ? "var(--muted)" : "var(--success)" }}>
                        {Math.round(myShare).toLocaleString("en-US")} ر
                      </div>
                      <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>نصيبي</div>
                    </div>
                  );
                })}
              </div>
              {elig?.is_eligible === false && (
                <div style={{ marginTop: 8, fontSize: 11, color: "#92400e", textAlign: "center" }}>
                  💡 الأرقام تحفيزية — ستستحقها بعد إكمال {elig.days_until_eligible} يوم
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Latest evaluation */}
      <div className="card" style={{ padding: 16 }} data-testid="profile-evaluation">
        <h3 style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>📊 آخر تقييم شهري</h3>
        {ev ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
              <MetricMini label="الشهر" value={`${ev.year}-${String(ev.month).padStart(2, "0")}`} />
              <MetricMini label="الدرجة النهائية" value={ev.final_score != null ? `${Math.round(ev.final_score)}/100` : "—"} color="var(--success)" />
              <MetricMini label="المتسوّق السري" value={ev.mystery_avg_score != null ? `${Math.round(ev.mystery_avg_score)}` : "—"} />
              <MetricMini label="إنذارات" value={ev.warnings_count} color={ev.warnings_count > 0 ? "var(--danger)" : "var(--text)"} />
              <MetricMini label="مخالفات" value={ev.violations_count} color={ev.violations_count > 0 ? "var(--warn)" : "var(--text)"} />
              <MetricMini label="مؤشّر المخاطرة" value={ev.risk_score != null ? Math.round(ev.risk_score) : "—"} />
            </div>
            {ev.notes_branch_manager && (
              <div style={{ marginTop: 10, padding: 10, background: "var(--beige-soft)", borderRadius: 8, fontSize: 12.5, lineHeight: 1.7 }}>
                <b>ملاحظات مدير الفرع:</b> {ev.notes_branch_manager}
              </div>
            )}
          </>
        ) : (
          <EmptyMsg text="لا يوجد تقييم معتمد بعد." />
        )}
      </div>

      {/* Annual evaluations history */}
      {evs.length > 1 && (
        <div className="card" style={{ padding: 16 }} data-testid="profile-eval-history">
          <h3 style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>📅 تقييمات آخر 12 شهر</h3>
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
            {evs.slice().reverse().map((e, i) => (
              <div key={i} style={{ minWidth: 78, padding: 8, background: "var(--beige-soft)", borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "var(--muted)" }}>{e.year}-{String(e.month).padStart(2, "0")}</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: (e.final_score || 0) >= 70 ? "var(--success)" : "var(--warn)" }}>{e.final_score != null ? Math.round(e.final_score) : "—"}</div>
                {e.warnings_count > 0 && <div style={{ fontSize: 10, color: "var(--danger)" }}>⚠ {e.warnings_count}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attendance */}
      <div className="card" style={{ padding: 16 }} data-testid="profile-attendance">
        <h3 style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>⏰ الحضور والانصراف (Malachite HCM)</h3>
        {att ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
            <MetricMini label="أيام العمل" value={att.work_days || att.workdays || "—"} />
            <MetricMini label="حضور" value={att.attended || att.present_days || "—"} color="var(--success)" />
            <MetricMini label="غياب" value={att.absent_days || att.absences || 0} color={(att.absent_days || 0) > 0 ? "var(--danger)" : "var(--text)"} />
            <MetricMini label="تأخير" value={att.late_count || att.delays || 0} color={(att.late_count || 0) > 0 ? "var(--warn)" : "var(--text)"} />
          </div>
        ) : (
          <EmptyMsg text="لا توجد بيانات حضور بعد — يتم التحديث تلقائياً من Malachite HCM." />
        )}
      </div>

      {/* Warnings */}
      <div className="card" style={{ padding: 16 }} data-testid="profile-warnings">
        <h3 style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>⚠️ الإنذارات المُصدرة ({wn.count})</h3>
        {wn.items.length > 0 ? (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
            {wn.items.map((w, i) => (
              <li key={i} style={{ padding: 10, background: "#fef2f2", borderRadius: 8, fontSize: 12.5, lineHeight: 1.7 }}>
                <b>{w.type || w.title || "إنذار"}</b>
                {w.date && <span style={{ color: "var(--muted)" }}> · {String(w.date).slice(0, 10)}</span>}
                {w.reason && <div style={{ marginTop: 4 }}>{w.reason}</div>}
              </li>
            ))}
          </ul>
        ) : (
          <EmptyMsg text="لا توجد إنذارات — استمر في العمل الممتاز 👏" />
        )}
      </div>

      {/* Training */}
      <div className="card" style={{ padding: 16 }} data-testid="profile-training">
        <h3 style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>🎓 ملفي التدريبي</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, marginBottom: 10 }}>
          <MetricMini label="إجمالي المُسنَدة" value={tr.assigned || 0} />
          <MetricMini label="مكتملة" value={tr.completed || 0} color="var(--success)" />
          <MetricMini label="معلّقة" value={tr.pending || 0} color={(tr.pending || 0) > 0 ? "var(--warn)" : "var(--text)"} />
        </div>
        {(tr.upcoming || []).length > 0 ? (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>📚 الاختبارات/التدريبات القادمة:</div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
              {tr.upcoming.map((t, i) => (
                <li key={i} style={{ padding: 10, background: "var(--beige-soft)", borderRadius: 8, fontSize: 12.5 }}>
                  <b>{t.title || t.course_name || "تدريب"}</b>
                  {t.due_date && <span style={{ color: "var(--muted)" }}> · موعد: {String(t.due_date).slice(0, 10)}</span>}
                </li>
              ))}
            </ul>
          </>
        ) : (
          (tr.assigned || 0) === 0 && <EmptyMsg text="لم تُسنَد لك تدريبات بعد." />
        )}
      </div>
    </div>
  );
}

function MetricMini({ label, value, color = "var(--text)" }) {
  return (
    <div style={{ padding: 10, background: "var(--beige-soft)", borderRadius: 8, textAlign: "center" }}>
      <div style={{ fontSize: 10, color: "var(--muted)" }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function EmptyMsg({ text }) {
  return <div style={{ padding: 12, textAlign: "center", color: "var(--muted)", fontSize: 12.5 }}>{text}</div>;
}

const inputStyle = {
  width: "100%", padding: "8px 10px", border: "1px solid var(--border)",
  borderRadius: 6, fontSize: 13, fontFamily: "inherit",
};
const btnSm = {
  padding: "5px 12px", border: "none", borderRadius: 4,
  fontSize: 12, fontWeight: 700, cursor: "pointer",
  background: "var(--cream-warm)", color: "var(--brown-deep)",
};

function unitArabic(u) {
  return { ml: "مل", l: "لتر", piece: "حبة", g: "جرام", kg: "كيلو" }[u] || u;
}
function translateAction(a) {
  return {
    submitted: "تقديم الطلب",
    approved_branch: "اعتماد مدير الفرع",
    approved_manager: "اعتماد مدير الفرع", // v187
    approved_ops: "اعتماد المالية",         // v187 — عرض قديم يُترجَم للمعنى الجديد
    approved_finance: "اعتماد المالية النهائي", // v187
    archived: "أُرشف في ملف التصدير الدفعي",
    rejected: "رفض",
  }[a] || a;
}
